/**
 * Tab targeting (modern WoW style). Pure: no DOM, no three.js; the caller injects the camera
 * projection and a line-of-sight test.
 *
 * Candidates: every alive hostile unit within `range` (40 yd), idle or fighting, not evading, in
 * line of sight and on screen (or, without a camera, in front of the character). Friendly NPCs
 * (town folk) are not units and never show up. Priority, strongest first:
 *   1. enemies engaged with the party (fighting us / on our threat tables / pack pulled) get a
 *      small head start (ENGAGED_BONUS), but idle mobs stay in the cycle (open-world grinding:
 *      Tab walks through the nearby mobs like WoW, in fields and dungeons alike);
 *   2. "near": far-away candidates rank after every near one (farther than 2× and 12 yd past
 *      the nearest);
 *   3. score = how close to the aim point on screen + how far off the character's facing +
 *      world distance (lower is better).
 * `TabCycler` walks the candidates WoW-style: Tab picks the best one not picked recently (so
 * two close scores can't ping-pong), Shift+Tab steps back through the picks, and the memory
 * resets after a pause or once every candidate was visited.
 */
import type { DeepReadonly, UnitId, Vec2, World } from '@keystone/sim';

export const TARGET_RANGE = 40;

export interface TargetingView {
  /** Screen pixel of a ground point at `height` yards; `visible` = in front of the camera and inside the viewport. */
  project(p: Vec2, height: number): { x: number; y: number; visible: boolean };
  /** Aim point (crosshair) in screen pixels, usually the viewport centre. */
  aim: { x: number; y: number };
  /** Distance normalizer in pixels (half the viewport diagonal). */
  radius: number;
}

export interface TargetingOptions {
  range?: number;
  view?: TargetingView | null;
  /** Line of sight between two ground points (walls); omitted = always clear. */
  los?: (a: Vec2, b: Vec2) => boolean;
  /** Only units in front of the character (soft target for an ability press). */
  frontOnly?: boolean;
}

export interface TargetCandidate {
  id: UnitId;
  dist: number;
  /** Engaged with the party (in combat with us). */
  engaged: boolean;
  /** Angle between the character's facing and the direction to the unit (0..π). */
  angle: number;
  /** Screen distance from the aim point, normalized (0 = on the crosshair); NaN without a view. */
  screen: number;
  score: number;
}

/** Score weights (lower total = better). */
export const TARGET_WEIGHTS = { screen: 0.45, facing: 0.25, dist: 0.3 } as const;
/** Head start for enemies already fighting the party (subtracted from their score). */
export const ENGAGED_BONUS = 0.12;
/** Half-angle of "in front" for the camera-less fallback and soft targeting. */
const FRONT_HALF_ANGLE = (100 * Math.PI) / 180;

type RO<T> = DeepReadonly<T>;
type U = NonNullable<RO<World>['units'][string]>;

function angleBetween(a: number, b: number): number {
  return Math.abs(Math.atan2(Math.sin(a - b), Math.cos(a - b)));
}

/** In combat with the party: on our threat tables, fighting one of us, or its pack was pulled. */
export function engagedWithParty(world: RO<World>, u: U, party: ReadonlySet<UnitId>): boolean {
  if (!u.inCombat) return false;
  const t = u.threat;
  if (t) {
    if (t.victimId && party.has(t.victimId)) return true;
    for (const id in t.entries) if (party.has(id)) return true;
  }
  if (u.targetId && party.has(u.targetId)) return true;
  const pack = u.packId ? world.packs?.[u.packId] : undefined;
  return pack?.state === 'engaged';
}

/** Valid tab candidates for `selfId`, best first (see the module comment for the rules). */
export function targetCandidates(world: RO<World>, selfId: UnitId, opts: TargetingOptions = {}): TargetCandidate[] {
  const self = world.units[selfId];
  if (!self) return [];
  const range = opts.range ?? TARGET_RANGE;
  const party = new Set<UnitId>(world.partyIds);
  const view = opts.view ?? null;
  const all: TargetCandidate[] = [];
  const front: TargetCandidate[] = [];
  for (const id of world.unitOrder) {
    const u = world.units[id];
    if (!u || !u.alive || u.faction === self.faction || u.id === selfId) continue;
    if (u.ai?.mode === 'evading') continue;
    const dx = u.pos.x - self.pos.x;
    const dy = u.pos.y - self.pos.y;
    const dist = Math.hypot(dx, dy);
    if (dist > range) continue;
    const angle = dist > 1e-6 ? angleBetween(Math.atan2(dy, dx), self.facing) : 0;
    if (opts.frontOnly && angle > FRONT_HALF_ANGLE) continue;
    if (opts.los && !opts.los(self.pos, u.pos)) continue;
    let screen = NaN;
    let onScreen = true;
    if (view) {
      const sp = view.project(u.pos, 1);
      onScreen = sp.visible;
      screen = Math.min(1.5, Math.hypot(sp.x - view.aim.x, sp.y - view.aim.y) / Math.max(1, view.radius));
    }
    const c: TargetCandidate = { id, dist, engaged: engagedWithParty(world, u, party), angle, screen, score: 0 };
    if (onScreen) all.push(c);
    else if (angle <= FRONT_HALF_ANGLE) front.push(c);
  }
  // Nothing on screen (camera looking away): fall back to what the character faces.
  const pool = all.length ? all : front;
  if (pool.length === 0) return [];
  const dmin = Math.min(...pool.map((c) => c.dist));
  const far = Math.max(dmin * 2, dmin + 12);
  for (const c of pool) {
    const facing = c.angle / Math.PI;
    const screen = Number.isNaN(c.screen) ? facing : c.screen;
    c.score = TARGET_WEIGHTS.screen * screen + TARGET_WEIGHTS.facing * facing + TARGET_WEIGHTS.dist * (c.dist / range) + (c.dist > far ? 1 : 0) - (c.engaged ? ENGAGED_BONUS : 0);
  }
  return pool.sort((a, b) => a.score - b.score || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** Best candidate (soft targeting / auto-retarget), or null. */
export function bestTarget(world: RO<World>, selfId: UnitId, opts: TargetingOptions = {}): UnitId | null {
  return targetCandidates(world, selfId, opts)[0]?.id ?? null;
}

/**
 * Tab / Shift+Tab cycling with a recent-targets memory (WoW keeps one so Tab doesn't bounce
 * back to a unit it just left). Deterministic given the candidate lists and times.
 */
export class TabCycler {
  /** The memory resets after this long without a Tab press... */
  memoryMs = 3500;
  /** ...or once the character moved / turned this much since the last press (a new look around). */
  moveResetYd = 5;
  turnResetRad = (50 * Math.PI) / 180;
  private recent: UnitId[] = [];
  private lastAt = -Infinity;
  private lastAnchor: { pos: Vec2; facing: number } | null = null;

  reset(): void {
    this.recent = [];
    this.lastAt = -Infinity;
    this.lastAnchor = null;
  }

  /** Remember a pick made elsewhere (click, soft target) so Tab continues from it. */
  note(id: UnitId | null, now: number): void {
    if (!id) return;
    if (now - this.lastAt > this.memoryMs) this.recent = [];
    this.lastAt = now;
    this.recent = this.recent.filter((r) => r !== id);
    this.recent.push(id);
  }

  next(cands: readonly TargetCandidate[], currentId: UnitId | null, reverse: boolean, now: number, anchor?: { pos: Vec2; facing: number }): UnitId | null {
    if (now - this.lastAt > this.memoryMs) this.recent = [];
    if (anchor) {
      const a = this.lastAnchor;
      if (a && (Math.hypot(anchor.pos.x - a.pos.x, anchor.pos.y - a.pos.y) > this.moveResetYd || angleBetween(anchor.facing, a.facing) > this.turnResetRad)) this.recent = [];
      this.lastAnchor = { pos: { x: anchor.pos.x, y: anchor.pos.y }, facing: anchor.facing };
    }
    this.lastAt = now;
    if (cands.length === 0) return null;
    const valid = new Set(cands.map((c) => c.id));
    this.recent = this.recent.filter((id) => valid.has(id));
    const cur = currentId && valid.has(currentId) ? currentId : null;
    if (cur && this.recent[this.recent.length - 1] !== cur) {
      this.recent = this.recent.filter((id) => id !== cur);
      this.recent.push(cur);
    }
    if (reverse) {
      // Step back through the picks; with no history, take the lowest-ranked candidate.
      if (cur && this.recent.length >= 2) {
        this.recent.pop();
        return this.recent[this.recent.length - 1]!;
      }
      const pick = [...cands].reverse().find((c) => c.id !== cur)?.id ?? cur;
      if (pick && pick !== cur) this.recent = [pick];
      return pick;
    }
    let pick = cands.find((c) => c.id !== cur && !this.recent.includes(c.id))?.id;
    if (!pick) {
      // Every candidate visited: start a new round from the best one (not the current).
      this.recent = cur ? [cur] : [];
      pick = cands.find((c) => c.id !== cur)?.id;
    }
    if (!pick) return cur;
    this.recent.push(pick);
    if (this.recent.length > 24) this.recent.shift();
    return pick;
  }
}

/** Line of sight on a walkability test: every ~1 yd sample between a and b must be walkable. */
export function walkableLos(isWalkable: (p: Vec2) => boolean, step = 1): (a: Vec2, b: Vec2) => boolean {
  return (a, b) => {
    const d = Math.hypot(b.x - a.x, b.y - a.y);
    const n = Math.max(1, Math.ceil(d / step));
    // Skip the endpoints' own footprint (units can stand at a wall's edge).
    for (let i = 1; i < n; i++) {
      const t = i / n;
      if (!isWalkable({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t })) return false;
    }
    return true;
  };
}
